// packages/web/src/components/OACatalogView.tsx — misma lógica (facetas, filtros con debounce, cursor, aprobar, crear).
// Cambios: filtros en barra, estado como pestañas, resultados en tabla densa, formulario "Nuevo" en panel
// con labels visibles (antes solo placeholders).
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, ThumbsUp, CheckCircle2, Search, X, Eye } from 'lucide-react';
import { api, type EnrichmentStatus, type EnrichmentTipo } from '../api';
import { OAEnrichmentDetail } from './OAEnrichmentDetail';
import { Alert, Button, Card, Field, INPUT_CLASS, PageHeader, StatusBadge, TABLE, TextareaField } from './ui';

interface OACatalogViewProps { siteId: string; onClose: () => void }

interface CatalogItem {
  id: number; mongo_oa_id: string; version_id: number; aprendizaje_central: string; tipo: EnrichmentTipo; status: EnrichmentStatus;
  asignatura: string | null; nivel_academico: string | null; oa_codigo: string | null; generated_by_ai_at: string; approved_at: string | null; created_at: string;
}

const STATUS: Record<EnrichmentStatus, { label: string; tone: 'ok' | 'warn' | 'muted' }> = {
  pending_curation: { label: 'Pendiente de curación', tone: 'warn' },
  approved: { label: 'Aprobado', tone: 'ok' },
  retired: { label: 'Retirado', tone: 'muted' },
};
const TIPO_LABEL: Record<EnrichmentTipo, string> = {
  conceptual: 'Conceptual', procedimental: 'Procedimental', argumentativo: 'Argumentativo', investigativo: 'Investigativo', hibrido: 'Híbrido',
} as Record<EnrichmentTipo, string>;
const TABS: { v: EnrichmentStatus | 'all'; l: string }[] = [
  { v: 'all', l: 'Todos' }, { v: 'pending_curation', l: 'Pendientes' }, { v: 'approved', l: 'Aprobados' }, { v: 'retired', l: 'Retirados' },
];

export function OACatalogView({ siteId, onClose }: OACatalogViewProps) {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<EnrichmentStatus | 'all'>('all');
  const [asigFilter, setAsigFilter] = useState('');
  const [nivelFilter, setNivelFilter] = useState('');
  const [query, setQuery] = useState('');
  const [facets, setFacets] = useState<{ asignaturas: string[]; niveles: string[] }>({ asignaturas: [], niveles: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  useEffect(() => { void api.getEnrichmentFacets().then(setFacets).catch(() => {}); }, []);

  const reload = useCallback(async (reset: boolean) => {
    if (reset) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      const opts: Parameters<typeof api.listEnrichments>[0] = { limit: 20 };
      if (statusFilter !== 'all') opts.status = statusFilter;
      if (asigFilter) opts.asignatura = asigFilter;
      if (nivelFilter) opts.nivel_academico = nivelFilter;
      if (query.trim()) opts.q = query.trim();
      if (!reset && cursor) opts.cursor = cursor;
      const r = await api.listEnrichments(opts);
      setItems((prev) => (reset ? r.items : [...prev, ...r.items]));
      setCursor(r.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  }, [statusFilter, asigFilter, nivelFilter, query, cursor]);

  useEffect(() => {
    const t = setTimeout(() => { void reload(true); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, asigFilter, nivelFilter, query]);

  const onApprove = async (id: number) => {
    try { await api.approveEnrichment(siteId, id); void reload(true); }
    catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };
  const clearFilters = () => { setStatusFilter('all'); setAsigFilter(''); setNivelFilter(''); setQuery(''); };
  const hasFilters = statusFilter !== 'all' || asigFilter !== '' || nivelFilter !== '' || query !== '';

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        back={{ label: 'Cerrar', onClick: onClose }}
        title="Catálogo OA"
        subtitle="Rúbricas curadas por sede. Cuando una está aprobada, el motor la reutiliza en vez de generar una nueva."
        actions={<Button variant={showCreate ? 'secondary' : 'primary'} onClick={() => setShowCreate(!showCreate)} leftIcon={showCreate ? <X aria-hidden className="w-4 h-4" /> : <Plus aria-hidden className="w-4 h-4" />}>{showCreate ? 'Cerrar formulario' : 'Nueva rúbrica'}</Button>}
      />

      {showCreate && <CreateEnrichmentForm siteId={siteId} onCreated={() => void reload(true)} />}

      <div className="space-y-3">
        <div role="tablist" aria-label="Estado" className="flex gap-1 border-b border-slate-200">
          {TABS.map((t) => (
            <button key={t.v} type="button" role="tab" aria-selected={statusFilter === t.v} onClick={() => setStatusFilter(t.v)}
              className={`-mb-px px-3.5 py-2 text-sm border-b-2 ${statusFilter === t.v ? 'border-marzano-600 text-marzano-700 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'} focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500`}>
              {t.l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search aria-hidden className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <label htmlFor="oa-q" className="sr-only">Buscar</label>
            <input id="oa-q" className={INPUT_CLASS + ' !mt-0 pl-9'} placeholder="Buscar OA o aprendizaje…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <label htmlFor="oa-asig" className="sr-only">Asignatura</label>
          <select id="oa-asig" className={INPUT_CLASS + ' !mt-0 !w-auto min-w-[180px]'} value={asigFilter} onChange={(e) => setAsigFilter(e.target.value)}>
            <option value="">Todas las asignaturas</option>
            {facets.asignaturas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <label htmlFor="oa-niv" className="sr-only">Nivel</label>
          <select id="oa-niv" className={INPUT_CLASS + ' !mt-0 !w-auto min-w-[160px]'} value={nivelFilter} onChange={(e) => setNivelFilter(e.target.value)}>
            <option value="">Todos los niveles</option>
            {facets.niveles.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} leftIcon={<X aria-hidden className="w-4 h-4" />}>Limpiar</Button>}
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}
      {loading && <p className="flex items-center gap-2 text-sm text-slate-700"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />Cargando…</p>}
      {!loading && items.length === 0 && !error && <Card><p className="text-sm text-slate-600 text-center py-4">Sin resultados para estos filtros.</p></Card>}

      {!loading && items.length > 0 && (
        <div className={TABLE.wrap}>
          <table className={TABLE.table}>
            <thead className={TABLE.thead}>
              <tr><th className={TABLE.th}>OA</th><th className={TABLE.th}>Aprendizaje central</th><th className={TABLE.th}>Estado</th><th className={TABLE.th}>Versión</th><th className={TABLE.th}><span className="sr-only">Acciones</span></th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className={TABLE.tr}>
                  <td className={TABLE.td + ' align-top whitespace-nowrap'}>
                    <span className="font-mono text-[13px] font-semibold text-marzano-700 bg-marzano-50 rounded px-1.5 py-0.5">{it.oa_codigo ?? '—'}</span>
                  </td>
                  <td className={TABLE.td + ' align-top max-w-[520px]'}>
                    <div className="font-medium text-slate-900 leading-snug text-pretty">{it.aprendizaje_central}</div>
                    <div className="text-[13px] text-slate-600 mt-0.5">{[it.asignatura, it.nivel_academico, TIPO_LABEL[it.tipo] ?? it.tipo].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className={TABLE.td + ' align-top'}>
                    <StatusBadge tone={STATUS[it.status].tone}>{STATUS[it.status].label}</StatusBadge>
                    {it.status === 'approved' && it.approved_at && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-slate-600"><CheckCircle2 aria-hidden className="w-3.5 h-3.5 text-emerald-700" />{new Date(it.approved_at).toLocaleDateString('es-CL')}</div>
                    )}
                  </td>
                  <td className={TABLE.td + ' align-top text-slate-700 whitespace-nowrap'}>v{it.version_id}<div className="text-xs text-slate-600">{new Date(it.created_at).toLocaleDateString('es-CL')}</div></td>
                  <td className={TABLE.td + ' align-top text-right whitespace-nowrap'}>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setDetailId(it.id)} leftIcon={<Eye aria-hidden className="w-4 h-4" />} title="Matriz Marzano completa: 4 dimensiones, preguntas y criterios">Ver matriz</Button>
                      {it.status === 'pending_curation' && <Button size="sm" onClick={() => void onApprove(it.id)} leftIcon={<ThumbsUp aria-hidden className="w-4 h-4" />}>Aprobar</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailId !== null && <OAEnrichmentDetail enrichmentId={detailId} siteId={siteId} onClose={() => setDetailId(null)} onApproved={() => void reload(true)} />}

      {cursor && !loading && (
        <div className="flex justify-center"><Button variant="secondary" size="sm" onClick={() => void reload(false)} loading={loadingMore}>Cargar más</Button></div>
      )}
    </div>
  );
}

function CreateEnrichmentForm({ siteId, onCreated }: { siteId: string; onCreated: () => void }) {
  const empty = { oa_codigo: '', oa_descripcion: '', asignatura: '', nivel_academico: '', hito_nombre: '', hito_descripcion: '', tipo: 'conceptual' as EnrichmentTipo };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(empty);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.createEnrichment(siteId, form); setForm(empty); onCreated(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit}>
      <Card title="Generar nueva rúbrica para curación"
        description="Se genera con IA en estado pendiente. El curriculista la revisa y la aprueba; desde ahí se usa para todas las evaluaciones del OA en esta sede.">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Código OA" placeholder="Ej: OA 3" value={form.oa_codigo} onChange={(e) => setForm({ ...form, oa_codigo: e.target.value })} required />
          <Field label="Asignatura" value={form.asignatura} onChange={(e) => setForm({ ...form, asignatura: e.target.value })} required />
          <Field label="Nivel" placeholder="Ej: 5° básico" value={form.nivel_academico} onChange={(e) => setForm({ ...form, nivel_academico: e.target.value })} required />
          <div>
            <label htmlFor="enr-tipo" className="block text-sm font-medium text-slate-800">Tipo de OA</label>
            <select id="enr-tipo" className={INPUT_CLASS} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as EnrichmentTipo })}>
              {(['conceptual', 'procedimental', 'argumentativo', 'investigativo', 'hibrido'] as EnrichmentTipo[]).map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
            </select>
          </div>
        </div>
        <TextareaField label="Descripción completa del OA" rows={2} value={form.oa_descripcion} onChange={(e) => setForm({ ...form, oa_descripcion: e.target.value })} required />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nombre del hito" value={form.hito_nombre} onChange={(e) => setForm({ ...form, hito_nombre: e.target.value })} required />
          <TextareaField label="Descripción del hito" rows={2} value={form.hito_descripcion} onChange={(e) => setForm({ ...form, hito_descripcion: e.target.value })} required />
        </div>
        {error && <Alert kind="error">{error}</Alert>}
        <div className="flex justify-end">
          <Button type="submit" loading={busy} leftIcon={<Plus aria-hidden className="w-4 h-4" />}>Generar y guardar como pendiente</Button>
        </div>
      </Card>
    </form>
  );
}
